import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { extractApiError, isRequestCanceled, unwrapApiResponse } from "../api/api";
import ReportTableCard from "./ReportTableCard";
import { useToast } from './ToastProvider';

const FALLBACK_REPORT_OPTIONS = [
  { key: "tnfe", label: "TNFE", description: "Personas físicas y morales" }
];

const FALLBACK_RANKING_TABS = [
  { key: "monto", label: "Top por monto" },
  { key: "operaciones", label: "Top por operaciones" }
];

function formatSectionTitle(section) {
  return section.label;
}

export default function ReportsView({ getToken, metadata }) {
  const { showError } = useToast();
  const reportOptions = metadata?.reportOptions || FALLBACK_REPORT_OPTIONS;
  const rankingTabs = metadata?.rankingTabs || FALLBACK_RANKING_TABS;
  const reportLimitOptions = metadata?.limitOptions || [25, 50, 100, 250];
  const tenantOptions = metadata?.tenantOptions || [];
  const defaultReport = metadata?.defaultReport || reportOptions[0]?.key || 'tnfe';
  const defaultLimit = metadata?.defaultLimit || reportLimitOptions[0] || 50;
  const defaultTenantId = metadata?.defaultTenantId || tenantOptions[0]?.value || '';

  const [activeReport, setActiveReport] = useState(defaultReport);
  const [limit, setLimit] = useState(defaultLimit);
  const [tenantId, setTenantId] = useState(defaultTenantId);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: "" });
  const [sidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeGroupBySection, setActiveGroupBySection] = useState({});
  const [activeRankingBySection, setActiveRankingBySection] = useState({});
  const requestRef = useRef(null);

  useEffect(() => {
    if (!reportOptions.some((item) => item.key === activeReport)) {
      setActiveReport(defaultReport);
    }
  }, [activeReport, defaultReport, reportOptions]);

  useEffect(() => {
    if (!tenantOptions.length) {
      setTenantId('');
      return;
    }

    if (!tenantOptions.some((item) => item.value === tenantId)) {
      setTenantId(defaultTenantId);
    }
  }, [defaultTenantId, tenantId, tenantOptions]);

  useEffect(() => {
    requestRef.current?.abort?.();
    const controller = new AbortController();
    requestRef.current = controller;

    async function loadReport() {
      try {
        setStatus({ loading: true, error: "" });
        const token = await getToken({ skipCache: true });
        const response = await api.get(`/query/reports/${activeReport}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit, tenantId },
          signal: controller.signal
        });

        const reportData = unwrapApiResponse(response);
        setData(reportData);
        setStatus({ loading: false, error: "" });

        const nextGroups = {};
        const nextRankings = {};

        (reportData?.sections || []).forEach((section) => {
          nextGroups[section.key] = section.groups?.[0]?.key || "";
          nextRankings[section.key] = rankingTabs[0]?.key || "monto";
        });

        setActiveGroupBySection(nextGroups);
        setActiveRankingBySection(nextRankings);
      } catch (error) {
        if (isRequestCanceled(error)) return;
        const message = extractApiError(error, "Error al cargar el reporte");
        setData(null);
        setStatus({ loading: false, error: message });
        showError(message);
      } finally {
        if (requestRef.current === controller) {
          requestRef.current = null;
        }
      }
    }

    if (activeReport && tenantId) {
      loadReport();
    }

    return () => controller.abort();
  }, [activeReport, getToken, limit, rankingTabs, showError, tenantId]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [activeReport]);

  const activeMeta = useMemo(
    () => reportOptions.find((item) => item.key === activeReport),
    [activeReport, reportOptions]
  );

  function updateSectionGroup(sectionKey, groupKey) {
    setActiveGroupBySection((prev) => ({ ...prev, [sectionKey]: groupKey }));
  }

  function updateSectionRanking(sectionKey, rankingKey) {
    setActiveRankingBySection((prev) => ({ ...prev, [sectionKey]: rankingKey }));
  }

  const sidebarContent = (
    <>
      <div className="report-sidebar-header">
        <div>
          <div className="brand-badge">Reportes</div>
          {!sidebarCollapsed ? (
            <h2 className="section-title with-margin">Opciones</h2>
          ) : null}
        </div>

        <div className="sidebar-header-actions">
          {!sidebarCollapsed ? (
            <>
              <label className="field small-field">
                <span>Entidad</span>
                <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                  {tenantOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="field small-field">
                <span>Top N</span>
                <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                  {reportLimitOptions.map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      {sidebarCollapsed ? (
        <div className="collapsed-limit-card">
          <span>Top</span>
          <strong>{limit}</strong>
        </div>
      ) : null}

      <div className="report-menu">
        {reportOptions.map((report) => (
          <button
            key={report.key}
            type="button"
            className={`report-menu-item ${report.key === activeReport ? "active" : ""}`}
            onClick={() => setActiveReport(report.key)}
            title={`${report.label} - ${report.description}`}
          >
            <strong>{report.label}</strong>
            {!sidebarCollapsed ? <span>{report.description}</span> : null}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <>
      <section className={`reports-layout ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
        <aside className="report-sidebar table-card desktop-report-sidebar">
          {sidebarContent}
        </aside>

        <div className="report-content report-content-expanded">
          <div className="mobile-report-toolbar">
            <button
              type="button"
              className="secondary-button full-width mobile-report-menu-button"
              onClick={() => setMobileMenuOpen(true)}
            >
              Abrir menú de reportes
            </button>

            <label className="field mobile-limit-field">
              <span>Entidad</span>
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                {tenantOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="field mobile-limit-field">
              <span>Top N</span>
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                {reportLimitOptions.map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </label>
          </div>

          <section className="hero-card report-hero">
            <div>
              <div className="brand-badge">{activeMeta?.label || "Reporte"}</div>
              <h1>{data?.title || activeMeta?.label}</h1>
              <p>{data?.description || activeMeta?.description}</p>
              {tenantId ? <p className="section-subtitle">Entidad activa: {tenantOptions.find((option) => option.value === tenantId)?.label || tenantId}</p> : null}
            </div>
          </section>

          {!tenantOptions.length ? (
            <section className="table-card">
              <p className="error-text">No hay entidades disponibles para este usuario.</p>
            </section>
          ) : null}

          {status.loading && tenantOptions.length ? (
            <div className="screen-loader">
              <div className="loader-card">
                <div className="spinner" />
                <p>Cargando Reporte...</p>
              </div>
            </div>
          ) : null}


          {tenantOptions.length ? data?.sections?.map((section) => {
            const activeGroupKey = activeGroupBySection[section.key] || section.groups?.[0]?.key;
            const activeGroup = section.groups.find((group) => group.key === activeGroupKey) || section.groups?.[0];
            const activeRanking = activeRankingBySection[section.key] || rankingTabs[0]?.key || "monto";
            const currentData = activeRanking === "monto" ? activeGroup?.topByMonto : activeGroup?.topByOperaciones;
            const currentTitle = activeRanking === "monto" ? "Top por monto" : "Top por número de operaciones";

            return (
              <section key={section.key} className="report-section-block table-card report-section-wide">
                <div className="report-section-header stacked-mobile">
                  <div>
                    <h2 className="section-title">{formatSectionTitle(section)}</h2>
                    <p className="section-subtitle">{section.groups.length} vista(s) disponibles</p>
                  </div>
                </div>

                <div className="tab-strip section-tab-strip">
                  {section.groups.map((group) => (
                    <button
                      key={group.key}
                      type="button"
                      className={`tab-button ${group.key === activeGroupKey ? "active" : ""}`}
                      onClick={() => updateSectionGroup(section.key, group.key)}
                    >
                      {group.label}
                    </button>
                  ))}
                </div>

                <div className="report-group-panel">
                  <div className="report-group-header stacked-mobile">
                    <div>
                      <h3 className="section-title">{activeGroup?.label}</h3>
                    </div>
                  </div>

                  <div className="tab-strip ranking-tab-strip">
                    {rankingTabs.map((ranking) => (
                      <button
                        key={ranking.key}
                        type="button"
                        className={`tab-button ${ranking.key === activeRanking ? "active" : ""}`}
                        onClick={() => updateSectionRanking(section.key, ranking.key)}
                      >
                        {ranking.label}
                      </button>
                    ))}
                  </div>

                  <ReportTableCard
                    title={currentTitle}
                    data={currentData || []}
                    table={activeGroup?.tableKey}
                    wide
                  />
                </div>
              </section>
            );
          }) : null}
        </div>
      </section>

      {mobileMenuOpen ? (
        <>
          <button
            type="button"
            className="mobile-report-backdrop"
            aria-label="Cerrar menú"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="mobile-report-drawer table-card">
            <div className="mobile-report-drawer-header">
              <h2 className="section-title">Reportes</h2>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setMobileMenuOpen(false)}
              >
                Cerrar
              </button>
            </div>

            <div className="mobile-report-drawer-body">
              {sidebarContent}
            </div>
          </aside>
        </>
      ) : null}
    </>
  );
}
